/**
 * API client for keepsake-gateway. Reads the JWT from localStorage (set by
 * the auth store on login) — same `aletheias_auth_token` key that vision-app
 * uses, so a single sign-in works across products. On 401 we clear the token
 * and bounce to /login.
 *
 * Existing call sites use `apiClient<T>(endpoint, options?)` and
 * `apiUpload<T>(endpoint, formData)`; signatures are preserved.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api'
const TOKEN_KEY = 'aletheias_auth_token'

/**
 * Error thrown for non-2xx responses. Carries the HTTP status and parsed
 * `detail` (string or object) so callers can branch on specific cases.
 * `.message` stays human-readable for the common `e.message` call sites.
 */
export class ApiError extends Error {
  status: number
  detail: any
  constructor(status: number, detail: any) {
    const message =
      typeof detail === 'string' ? detail : detail?.reason || `API error ${status}`
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(TOKEN_KEY)
}

function handleUnauthorized() {
  if (typeof window === 'undefined') return
  localStorage.removeItem(TOKEN_KEY)
  if (!window.location.pathname.startsWith('/login')) {
    const redirect = window.location.pathname + window.location.search
    window.location.assign(`/login?redirect=${encodeURIComponent(redirect)}`)
  }
}

export async function apiClient<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = getAuthToken()

  const response = await fetch(`${API_BASE}/keepsake${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  })

  if (response.status === 401) {
    handleUnauthorized()
    throw new Error('Session expired — redirecting to login')
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new ApiError(response.status, error.detail)
  }

  return response.json()
}

export async function apiUpload<T = any>(endpoint: string, formData: FormData): Promise<T> {
  const token = getAuthToken()

  const response = await fetch(`${API_BASE}/keepsake${endpoint}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  })

  if (response.status === 401) {
    handleUnauthorized()
    throw new Error('Session expired — redirecting to login')
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new ApiError(response.status, error.detail)
  }

  return response.json()
}
